import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Clipboard, 
  Clock,
  CheckCircle,
  AlertTriangle,
  User,
  Calendar,
  DollarSign,
  FileText,
  Target,
  TrendingUp,
  MoreHorizontal,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';
import WorkOrderService from '../../services/workOrderService';

// Custom Dropdown Component - exactly like Task Management
const ActionDropdown = ({ workOrder, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOnButton = buttonRef.current && buttonRef.current.contains(event.target);
      const menuElement = document.querySelector('.dropdown-menu.show');
      const clickedOnMenu = menuElement && menuElement.contains(event.target);
      
      if (!clickedOnButton && !clickedOnMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 120;
      
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    if (workOrder && (workOrder.id || workOrder._id)) {
      onView(workOrder);
    } else {
      console.error('Work order data is missing or invalid:', workOrder);
    }
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    if (workOrder && (workOrder.id || workOrder._id)) {
      onEdit(workOrder);
    } else {
      console.error('Work order data is missing or invalid:', workOrder);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    if (workOrder && (workOrder.id || workOrder._id)) {
      onDelete(workOrder);
    } else {
      console.error('Work order data is missing or invalid:', workOrder);
    }
  };

  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: openUpward ? window.innerHeight - rect.top + 8 : rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, openUpward]);

  const dropdownMenu = isOpen ? (
    <ul className="dropdown-menu dropdown-menu-end show" style={{
      position: 'fixed',
      [openUpward ? 'bottom' : 'top']: `${menuPosition.top}px`,
      right: `${menuPosition.right}px`,
      zIndex: 999999,
      minWidth: '180px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      borderRadius: '0',
      padding: '0',
      backgroundColor: '#ffffff',
      listStyle: 'none',
      margin: 0,
      overflow: 'hidden',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      isolation: 'isolate'
    }}>
      <li style={{ 
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        border: 'none',
        isolation: 'isolate'
      }}>
        <button
          type="button"
          className="dropdown-item d-flex align-items-center"
          onClick={handleView}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#374151',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Eye size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
          View Details
        </button>
      </li>
      <li style={{ 
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        border: 'none',
        isolation: 'isolate'
      }}>
        <button
          type="button"
          className="dropdown-item d-flex align-items-center"
          onClick={handleEdit}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#374151',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Edit size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
          Edit
        </button>
      </li>
      <li style={{ 
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        border: 'none',
        isolation: 'isolate'
      }}>
        <button
          type="button"
          className="dropdown-item d-flex align-items-center"
          onClick={handleDelete}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#374151',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Trash2 size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
          Delete
        </button>
      </li>
    </ul>
  ) : null;

  return (
    <>
      <div className="dropdown" ref={dropdownRef} style={{ position: 'relative', zIndex: 1 }}>
        <button
          ref={buttonRef}
          className="btn btn-outline-secondary btn-sm"
          type="button"
          onClick={handleToggle}
          style={{
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            color: '#3A8DCC',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            height: '32px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#E8F1FD';
            e.target.style.borderColor = '#3A8DCC';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
        </button>
      </div>
      {isOpen && createPortal(dropdownMenu, document.body)}
    </>
  );
};

const getDisplayName = (value, fallback = 'N/A') => {
  if (!value) return fallback;

  if (typeof value === 'string') return value;

  if (typeof value === 'object') {
    return (
      value.name ||
      value.email ||
      value.title ||
      fallback
    );
  }

  return fallback;
};

const WorkOrders = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workOrderToDelete, setWorkOrderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  // Transform backend work order data to frontend format
  const transformWorkOrder = (workOrder) => {
    // Calculate progress (simplified - can be enhanced based on actual progress tracking)
    let progress = 0;
    if (workOrder.status === 'completed') {
      progress = 100;
    } else if (workOrder.status === 'in-progress') {
      // Calculate based on dates or use a default
      if (workOrder.startDate && workOrder.endDate) {
        const start = new Date(workOrder.startDate);
        const end = new Date(workOrder.endDate);
        const now = new Date();
        if (now >= start && now <= end) {
          const total = end - start;
          const elapsed = now - start;
          progress = Math.min(100, Math.round((elapsed / total) * 100));
        }
      } else {
        progress = 50; // Default for in-progress
      }
    }

    // Get subcontractor name
    const subcontractorName = getDisplayName(workOrder.subcontractor);
    const assignedToName = getDisplayName(workOrder.assignedTo);

    // Map status from backend to display format
    const statusMap = {
      'draft': 'Draft',
      'pending': 'Pending',
      'approved': 'Approved',
      'in-progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };

    // Map priority from backend to display format
    const priorityMap = {
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High',
      'urgent': 'Urgent'
    };

    return {
      id: workOrder.workOrderNumber || workOrder._id,
      _id: workOrder._id,
      workOrderNumber: workOrder.workOrderNumber,
      title: workOrder.title || '',
      description: workOrder.description || '',
      subcontractor: subcontractorName,
      assignedTo: assignedToName,
      status: statusMap[workOrder.status] || workOrder.status || 'Draft',
      statusValue: workOrder.status, // Keep original for filtering
      priority: priorityMap[workOrder.priority] || workOrder.priority || 'Medium',
      priorityValue: workOrder.priority, // Keep original for filtering
      startDate: workOrder.startDate ? new Date(workOrder.startDate).toISOString().split('T')[0] : '',
      dueDate: workOrder.deadline ? new Date(workOrder.deadline).toISOString().split('T')[0] : 
               (workOrder.endDate ? new Date(workOrder.endDate).toISOString().split('T')[0] : ''),
      budget: workOrder.budget || workOrder.estimatedCost || 0,
      spent: workOrder.actualCost || 0,
      progress: progress,
      location: workOrder.location || workOrder.siteAddress || '',
      workOrderData: workOrder // Keep full data for reference
    };
  };

  useEffect(() => {
    loadWorkOrders();
  }, [pagination.page, searchTerm, statusFilter, priorityFilter]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        loadWorkOrders();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadWorkOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page || 1,
        limit: pagination.itemsPerPage || 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(priorityFilter && { priority: priorityFilter })
      };

      const response = await WorkOrderService.getWorkOrders(params);
      
      if (response && response.success) {
        const transformedWorkOrders = (response.data || []).map(transformWorkOrder);
        setWorkOrders(transformedWorkOrders);
        
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.current || response.pagination.page || 1,
            pages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            limit: response.pagination.limit || 10
          }));
        }
      } else {
        setWorkOrders([]);
        if (response && !response.data) {
          showError('Failed to load work orders.');
        }
      }
    } catch (err) {
      console.error('Error loading work orders:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load work orders.';
      showError(errorMessage);
      setWorkOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadWorkOrders();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/work-orders';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh work orders list',
      disabled: loading
    },
    {
      label: 'Create Work Order',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/work-orders/create'),
      variant: 'primary',
      tooltip: 'Create a new work order'
    }
  ];

  const handleView = (workOrder) => {
    const workOrderId = workOrder._id || workOrder.id;
    if (workOrderId) {
      navigate(`/subcontracting/work-orders/view/${workOrderId}`);
    } else {
      console.error('Work order ID not found:', workOrder);
      showError('Unable to view work order: ID not found');
    }
  };

  const handleEdit = (workOrder) => {
    const workOrderId = workOrder._id || workOrder.id;
    if (workOrderId) {
      navigate(`/subcontracting/work-orders/edit/${workOrderId}`);
    } else {
      console.error('Work order ID not found:', workOrder);
      showError('Unable to edit work order: ID not found');
    }
  };

  const handleDelete = (workOrder) => {
    setWorkOrderToDelete(workOrder);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!workOrderToDelete) return;
    
    setIsDeleting(true);
    try {
      const workOrderId = workOrderToDelete._id || workOrderToDelete.id;
      if (!workOrderId) {
        throw new Error('Work order ID not found');
      }
      
      await WorkOrderService.deleteWorkOrder(workOrderId);
      success('Work order deleted successfully!');
      await loadWorkOrders();
      setShowDeleteModal(false);
      setWorkOrderToDelete(null);
    } catch (err) {
      console.error('Error deleting work order:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete work order.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
    'Draft':        { color: '#6b7280', bg: '#f3f4f6', text: 'Draft' },
    'Pending':      { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
    'Approved':     { color: '#3b82f6', bg: '#dbeafe', text: 'Approved' },
    'In Progress':  { color: '#6366f1', bg: '#e0e7ff', text: 'In Progress' },
    'Completed':    { color: '#10b981', bg: '#d1fae5', text: 'Completed' },
    'Cancelled':    { color: '#ef4444', bg: '#fee2e2', text: 'Cancelled' },
    };
    
  const config = statusConfig[status] || statusConfig['Draft'];

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
        whiteSpace: 'nowrap'
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {config.text}
    </span>
  );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
    'Urgent': { color: '#dc2626', bg: '#fee2e2', text: 'Urgent' },
    'High':   { color: '#f97316', bg: '#ffedd5', text: 'High' },
    'Medium': { color: '#3b82f6', bg: '#dbeafe', text: 'Medium' },
    'Low':    { color: '#6b7280', bg: '#f3f4f6', text: 'Low' },
    };
    
  const config = priorityConfig[priority] || priorityConfig['Medium'];

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
        whiteSpace: 'nowrap'
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {config.text}
    </span>
  );
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'workOrderNumber',
      label: 'Work Order',
      render: (value, workOrder) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={workOrder.workOrderNumber || workOrder.id}>
            {workOrder.workOrderNumber || workOrder.id}
          </div>
          <div className="text-muted small text-truncate" style={{ maxWidth: '150px' }} title={workOrder.title}>
            {workOrder.title}
          </div>
        </div>
      )
    },
    {
      key: 'subcontractor',
      label: 'Subcontractor',
      render: (value, workOrder) => (
        <div>
          <div className="fw-medium text-truncate" style={{ maxWidth: '120px' }} title={workOrder.subcontractor}>
            {workOrder.subcontractor}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={`Assigned to: ${workOrder.assignedTo}`}>
            Assigned to: {workOrder.assignedTo}
          </small>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, workOrder) => getStatusBadge(workOrder.status)
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value, workOrder) => getPriorityBadge(workOrder.priority)
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (value, workOrder) => (
        <div className="d-flex align-items-center">
          <div className="progress me-2" style={{ width: '60px', height: '8px' }}>
            <div 
              className="progress-bar bg-success" 
              style={{ width: `${workOrder.progress}%` }}
            ></div>
          </div>
          <span className="fw-medium">{workOrder.progress}%</span>
        </div>
      )
    },
    {
      key: 'budget',
      label: 'Budget',
      render: (value, workOrder) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={`$${workOrder.budget.toLocaleString()}`}>
            ${workOrder.budget.toLocaleString()}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title={`Spent: $${workOrder.spent.toLocaleString()}`}>
            Spent: ${workOrder.spent.toLocaleString()}
          </small>
        </div>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (value, workOrder) => {
        const dueDate = workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString() : 'N/A';
        const startDate = workOrder.startDate ? new Date(workOrder.startDate).toLocaleDateString() : 'N/A';
        return (
          <div>
            <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={dueDate}>
              {dueDate}
            </div>
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title={`Start: ${startDate}`}>
              Start: {startDate}
            </small>
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, workOrder) => {
        if (!workOrder || !workOrder.id) {
          console.error('Invalid work order data in render function:', workOrder);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown 
            workOrder={workOrder}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Work Orders"
        description="Create, assign, and track work orders for subcontractors"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Work Orders Table */}
      <DataTable
        data={workOrders}
        columns={columns}
        title="Work Orders"
        loading={loading}
        pagination={{
          currentPage: pagination.current || pagination.page || 1,
          totalPages: pagination.pages || 1,
          total: pagination.totalItems || 0,
          itemsPerPage: pagination.limit || pagination.itemsPerPage || 10
        }}
        onPageChange={(page) => {
          setPagination(prev => ({
            ...prev,
            page: page,
            current: page
          }));
        }}
        searchPlaceholder="Search work orders..."
        searchable={true}
        emptyMessage="No work orders found. Click 'Create Work Order' to get started."
        showActions={false}
      />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Work Order"
        message={`Are you sure you want to delete "${workOrderToDelete?.workOrderNumber || workOrderToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default WorkOrders;
